import { create } from "zustand";

type AdminOrderTab =
  | "CURRENT"
  | "DELIVERED"
  | "RETURNED"
  | "CANCELLED";

interface ReviewEntity {
  entityType: "shipment" | "shipment_item";
  shipmentId?: number;
  shipmentItemId?: number;
  orderId?: number;
  info?: any;
}

interface AdminOrderFilters {
  fulfillmentStatus: string;
  dateFrom: string;
  dateTo: string;
  minAmount: string;
  maxAmount: string;
  shipmentStatus: string;
  deliveryMethod: string;
  search: string;
  ordering: string;
}

interface AdminOrderState extends AdminOrderFilters {
  activeTab: AdminOrderTab;

  currentPage: number;
  pageSize: number;

  reviewEntity: ReviewEntity | null;
  reviewData: any | null;
  isReviewViewModalOpen: boolean;

  setActiveTab: (tab: AdminOrderTab) => void;
  setFilter: (key: keyof AdminOrderFilters, value: string) => void;
  setCurrentPage: (page: number) => void;
  resetFilters: () => void;
  getFilters: () => AdminOrderFilters;

  openViewReview: (entity: ReviewEntity, review: any) => void;
  closeReviewModal: () => void;
}

const initialFilters: AdminOrderFilters = {
  fulfillmentStatus: "",
  dateFrom: "",
  dateTo: "",
  minAmount: "",
  maxAmount: "",
  shipmentStatus: "",
  deliveryMethod: "",
  search: "",
  ordering: "-created_at",
};

export const useAdminOrderStore = create<AdminOrderState>((set, get) => ({
  activeTab: "CURRENT",

  currentPage: 1,
  pageSize: 10,

  ...initialFilters,

  reviewEntity: null,
  reviewData: null,
  isReviewViewModalOpen: false,

  setActiveTab: (tab) =>
    set((state) => {
      if (state.activeTab === tab) {
        return state;
      }

      return {
        activeTab: tab,
        currentPage: 1,
        shipmentStatus: "",
      };
    }),

  setFilter: (key, value) =>
    set((state) => {
      if (state[key] === value) {
        return state;
      }

      return {
        [key]: value,
        currentPage: 1,
      } as Partial<AdminOrderState>;
    }),

  setCurrentPage: (page) =>
    set((state) => {
      if (state.currentPage === page) {
        return state;
      }

      return {
        currentPage: page,
      };
    }),

  resetFilters: () =>
    set((state) => {
      const alreadyEmpty =
        state.fulfillmentStatus === "" &&
        state.dateFrom === "" &&
        state.dateTo === "" &&
        state.minAmount === "" &&
        state.maxAmount === "" &&
        state.shipmentStatus === "" &&
        state.deliveryMethod === "" &&
        state.search === "" &&
        state.ordering === "-created_at";

      if (alreadyEmpty && state.currentPage === 1) {
        return state;
      }

      return {
        ...initialFilters,
        currentPage: 1,
      };
    }),

  getFilters: () => {
    const state = get();

    return {
      fulfillmentStatus: state.fulfillmentStatus,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      minAmount: state.minAmount,
      maxAmount: state.maxAmount,
      shipmentStatus: state.shipmentStatus,
      deliveryMethod: state.deliveryMethod,
      search: state.search,
      ordering: state.ordering,
    };
  },

  openViewReview: (entity, review) =>
    set({
      reviewEntity: entity,
      reviewData: review,
      isReviewViewModalOpen: true,
    }),

  closeReviewModal: () =>
    set({
      reviewEntity: null,
      reviewData: null,
      isReviewViewModalOpen: false,
    }),
}));
